import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageTransition } from '@/components/PageTransition';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useSpaceData } from '@/hooks/useSpaceData';
import {
  ChevronLeft,
  Loader2,
  Mail,
  MapPin,
  Briefcase,
  User,
  Save,
  AlertCircle
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';

// Normalize role values from database to match dropdown options
const normalizeRole = (role) => {
  if (!role) return 'User';
  const normalized = role.toLowerCase();
  if (normalized === 'admin' || normalized === 'administrator') return 'Admin';
  if (normalized === 'manager') return 'Manager';
  if (normalized === 'employee' || normalized === 'user') return 'User';
  // Default to User if unrecognized
  return 'User';
};

export function EditTeamMemberPage() {
  const navigate = useNavigate();
  const { projectId, memberId } = useParams();
  const { company, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);

  // Use the cached space data hook
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools: cachedTools, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(member ? `Edit ${member.first_name} ${member.last_name}` : 'Edit Team Member');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('User');
  const [department, setDepartment] = useState('');
  const [status, setStatus] = useState('active');

  useEffect(() => {
    if (cachedTools) {
      setSpaceTools(cachedTools);
    }
  }, [cachedTools]);

  useEffect(() => {
    if (memberId && projectId) {
      loadMemberData();
    }
  }, [memberId, projectId]);

  const loadMemberData = async () => {
    setLoading(true);
    try {
      // Load member from space_members + user_profiles
      const { data: memberData, error: memberError } = await supabase
        .from('space_members')
        .select(`
          user_id,
          status,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            role,
            job_title,
            department
          )
        `)
        .eq('user_id', memberId)
        .eq('project_id', projectId)
        .single();

      if (memberError) {
        console.error('Error loading member:', memberError);
        toast.error('Member not found');
        navigate(`/space/${projectId}/team`);
        return;
      }

      // Transform to expected format
      const formattedMember = {
        id: memberData.user_id,
        name: `${memberData.user_profiles.first_name || ''} ${memberData.user_profiles.last_name || ''}`.trim() || memberData.user_profiles.email,
        email: memberData.user_profiles.email,
        role: memberData.user_profiles.role,
        department: memberData.user_profiles.department,
        status: memberData.status,
        avatar_url: memberData.user_profiles.avatar_url,
        user_id: memberData.user_id
      };

      setMember(formattedMember);
      setName(formattedMember.name || '');
      setEmail(formattedMember.email || '');
      setRole(normalizeRole(formattedMember.role));
      setDepartment(formattedMember.department || '');
      setStatus(formattedMember.status || 'active');
    } catch (error) {
      console.error('Error loading member:', error);
      toast.error('Failed to load member details');
      navigate(`/space/${projectId}/team`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Parse name into first/last
      const nameParts = name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Update user_profiles (identity data including department)
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          email: email,
          role: role,
          department: department || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', memberId);

      if (profileError) throw profileError;

      // Update space_members (space-specific data)
      const { error: spaceError } = await supabase
        .from('space_members')
        .update({
          status: status,
          updated_date: new Date().toISOString()
        })
        .eq('user_id', memberId)
        .eq('project_id', projectId);

      if (spaceError) throw spaceError;

      toast.success('Member updated successfully');
      navigate(`/space/${projectId}/team`);
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Failed to save member changes');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  // Parent breadcrumb for Team
  const getParentBreadcrumb = () => {
    return {
      name: 'Team',
      onClick: () => navigate(`/space/${projectId}/team`)
    };
  };

  if (loading) {
    return (
      <AppShell3 pageTitle="" pageDescription="">
        <ToolHeader
          spaceName={space?.name || 'Space'}
          toolName="Edit Team Member"
          currentTool="team"
          spaceTools={spaceTools}
          parentBreadcrumb={getParentBreadcrumb()}
        />
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner loading={loading} size={20} />
        </div>
      </AppShell3>
    );
  }

  if (!member) {
    return (
      <AppShell3 pageTitle="" pageDescription="">
        <ToolHeader
          spaceName={space?.name || 'Space'}
          toolName="Edit Team Member"
          currentTool="team"
          spaceTools={spaceTools}
          parentBreadcrumb={getParentBreadcrumb()}
        />
        <div className="max-w-7xl mx-auto mt-8">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Member Not Found</h2>
            <p className="text-gray-600 mb-6">The team member you're looking for doesn't exist or you don't have permission to edit them.</p>
            <Button onClick={() => navigate(`/space/${projectId}/team`)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Team
            </Button>
          </Card>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle="" pageDescription="">
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          toolName={`Edit: ${member?.name}`}
          currentTool="team"
          spaceTools={spaceTools}
          parentBreadcrumb={getParentBreadcrumb()}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/space/${projectId}/team`)}
          className="mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Team
        </Button>

        <div className="space-y-6">
          {/* Member Avatar and Basic Info Header */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={member?.avatar_url} alt={member?.name} />
                  <AvatarFallback className="text-xl">
                    {getInitials(member?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <CardTitle className="text-2xl">{member?.name}</CardTitle>
                  <CardDescription className="text-base mt-1">{member?.email}</CardDescription>
                  <Badge variant={status === 'active' ? 'default' : 'secondary'} className="mt-2">
                    {status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Member Information */}
          <Card>
            <CardHeader>
              <CardTitle>Member Information</CardTitle>
              <CardDescription>Update team member details for this space</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">
                    <User className="inline h-3 w-3 mr-1" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter full name"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="email">
                    <Mail className="inline h-3 w-3 mr-1" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="role">
                    <Briefcase className="inline h-3 w-3 mr-1" />
                    Role
                  </Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="User">User</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="department">
                    <Briefcase className="inline h-3 w-3 mr-1" />
                    Department
                  </Label>
                  <Input
                    id="department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="Enter department"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pb-8">
            <Button
              variant="outline"
              onClick={() => navigate(`/space/${projectId}/team`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              size="lg"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}

export default EditTeamMemberPage;
